// Ways-to-win 評估 — 對應 Unity GameLogic.GetPossibleWays（累乘各輪符號數，Tracker 頂輪疊加於主輪 1-4）。
// 由最左輪（reel 0）起算，需連續輪都命中；未達 MIN_WAYS_REELS 不計中獎。
// [ASSUMED] 確切演算法細節（例如是否有其他排除規則）未逐行核對來源，見 docs/PIXI_MIGRATION_PLAN.md 風險表。
//
// Tracker 頂輪疊加慣例（本專案內部約定，非來源逐行核對）：trackerColumn 長度固定等於
// TRACKER_OVERLAY_REELS.length（4），一格對應一個主輪（index i ↔ TRACKER_OVERLAY_REELS[i]），
// 而不是把整條 tracker 欄位廣播套用到每個主輪。

import { MAIN_REEL_COUNT, MIN_WAYS_REELS, TRACKER_REEL_INDEX, trackerPositionForReel, WILD_SYMBOL_ID } from '../constants'
import { PAYABLE_SYMBOL_IDS } from '../config/symbols'
import type { Board, CellRef, ReelColumn } from '../model/board'

export interface WayWin {
  readonly symbolId: number
  readonly reelsMatched: number
  readonly ways: number
  readonly cells: readonly CellRef[]
}

function matchesInReel(
  board: Board,
  trackerColumn: ReelColumn,
  reelIndex: number,
  isMatch: (symbolId: number) => boolean,
): CellRef[] {
  const cells: CellRef[] = []
  board[reelIndex]!.forEach((cell, row) => {
    if (isMatch(cell.symbol)) cells.push({ reel: reelIndex, row })
  })
  // Tracker 頂輪「反序」疊加：主輪 reelIndex 對應的頂輪格由 trackerPositionForReel 決定（非被覆蓋主輪回 -1）。
  // 取格與記錄命中 row 都用同一個 position，確保與畫面（ReelsView.trackerCellX）及消除補位一致。見 constants 說明。
  const trackerPosition = trackerPositionForReel(reelIndex)
  if (trackerPosition !== -1) {
    const trackerCell = trackerColumn[trackerPosition]
    if (trackerCell !== undefined && isMatch(trackerCell.symbol)) {
      cells.push({ reel: TRACKER_REEL_INDEX, row: trackerPosition })
    }
  }
  return cells
}

function evaluateSymbol(
  board: Board,
  trackerColumn: ReelColumn,
  symbolId: number,
  isMatch: (cellSymbol: number) => boolean,
): WayWin | null {
  let ways = 1
  let reelsMatched = 0
  const cells: CellRef[] = []

  for (let reel = 0; reel < MAIN_REEL_COUNT; reel++) {
    const reelCells = matchesInReel(board, trackerColumn, reel, isMatch)
    if (reelCells.length === 0) break
    ways *= reelCells.length
    reelsMatched += 1
    cells.push(...reelCells)
  }

  if (reelsMatched < MIN_WAYS_REELS) return null
  return { symbolId, reelsMatched, ways, cells }
}

/**
 * 評估整個盤面的 ways-to-win 中獎清單。Wild 可替代一般符號參與連線；
 * 另外單獨評估「純 Wild」連線（不替代）。Scatter 不參與 ways 評估（獨立以數量觸發 Free Game）。
 */
export function evaluateWays(board: Board, trackerColumn: ReelColumn): WayWin[] {
  const wins: WayWin[] = []

  for (const symbolId of PAYABLE_SYMBOL_IDS) {
    if (symbolId === WILD_SYMBOL_ID) continue
    const win = evaluateSymbol(
      board,
      trackerColumn,
      symbolId,
      (cellSymbol) => cellSymbol === symbolId || cellSymbol === WILD_SYMBOL_ID,
    )
    if (win !== null) wins.push(win)
  }

  const wildWin = evaluateSymbol(board, trackerColumn, WILD_SYMBOL_ID, (cellSymbol) => cellSymbol === WILD_SYMBOL_ID)
  if (wildWin !== null) wins.push(wildWin)

  return wins
}
